// PN-7 (T-430) — UNE liste de statuts pour le menu de la fiche ET pour son badge : celle du tenant (metier_statuts_config, lue par
// useStatutsConfig → /api/metier/statuts-config/frontend-config, déjà appelé par l'écran). Plus de liste écrite en dur dans le JSX,
// plus de code brut affiché. Rien n'est écrit ici : l'écriture reste PATCH /dossiers/{id}/statut → set_dossier_statut.

// Codes qui demandent une RAISON quand ils sont posés (sortie du parcours). Complété par les étapes du pipeline marquées
// `requires_closure_reason` (refuse, abandonne). Repli minimal pour un tenant sans pipeline.
const A_RAISON_PAR_DEFAUT = ["abandonne", "refuse_anah", "refuse", "archive", "annule"];

/** Libellé lisible d'un code qui n'a pas de libellé : « visite_faite » → « Visite faite ». Jamais le code brut. */
export const lisible = (code) => {
  const t = String(code || "").replace(/_/g, " ").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "—";
};

/** Table code → { label } du tenant : celle de l'activité du dossier, sinon celle de TEF (comportement historique de l'écran). */
export function tableStatuts(config, activite) {
  const par = config?.statuts || {};
  const t = par[activite];
  return t && Object.keys(t).length ? t : par.tef || {};
}

export function exigeRaison(code, pipeline) {
  const etape = (pipeline?.steps || []).find((s) => s.code === code || (pipeline.legacy_mapping || {})[code] === s.code);
  return !!etape?.requires_closure_reason || A_RAISON_PAR_DEFAUT.includes(code);
}

/** Étape du pipeline d'un code de statut : le code est une étape, ou le `legacy_mapping` l'y rattache ; sinon null (jamais de repli vers « cree »). */
function etapeDuCode(code, pipeline) {
  const steps = pipeline?.steps || [];
  if (steps.some((s) => s.code === code)) return code;
  const via = (pipeline?.legacy_mapping || {})[code];
  return via && steps.some((s) => s.code === via) ? via : null;
}

/** Ordre de configuration (`ordre`, qui recommence à 1 dans chaque groupe de la configuration) : départage seulement à l'intérieur d'une étape. */
const ordreDeConfig = (config, code, rang) => {
  const ligne = (config?.all_statuts || []).find((x) => x.code === code);
  return typeof ligne?.ordre === "number" ? ligne.ordre : 1000 + rang;
};

/**
 * T-430b (relecture T-430R) — le menu « Changer le statut » : TOUS les statuts configurés du tenant (rien n'est retiré de la configuration), triés par
 * ÉTAPE du pipeline puis par `ordre`, regroupés sous le libellé de l'étape. Les statuts qu'aucun dossier du tenant ne porte (`config.statuts_utilises`),
 * et ceux qu'aucune étape ne reconnaît, vont dans un dernier groupe replié « Autres statuts ». Le statut actuel du dossier n'y est jamais rangé.
 * Retourne [{ cle, label, replie, entrees: [{ code, label, raison }] }]. `statuts_utilises` inconnu (ancien serveur) : tout est « utilisé ».
 */
export function groupesStatuts(config, activite, pipeline, statutActuel = null) {
  const utilises = Array.isArray(config?.statuts_utilises) ? new Set(config.statuts_utilises) : null;
  const steps = pipeline?.steps || [];
  const rangs = new Map(steps.map((s, i) => [s.code, i]));
  const groupes = new Map();
  const autres = [];
  Object.entries(tableStatuts(config, activite)).forEach(([code, info], rang) => {
    const entree = { code, label: info?.label || lisible(code), raison: exigeRaison(code, pipeline), _ordre: ordreDeConfig(config, code, rang) };
    const etape = etapeDuCode(code, pipeline);
    const porte = !utilises || utilises.has(code) || code === statutActuel;
    if (!etape || !porte) { autres.push(entree); return; }
    if (!groupes.has(etape)) groupes.set(etape, []);
    groupes.get(etape).push(entree);
  });
  const nettoyer = (liste) => liste.sort((a, b) => a._ordre - b._ordre).map(({ _ordre, ...e }) => e);
  const resultat = [...groupes.entries()]
    .sort(([a], [b]) => rangs.get(a) - rangs.get(b))
    .map(([etape, liste]) => ({ cle: etape, label: steps.find((s) => s.code === etape)?.label || lisible(etape), replie: false, entrees: nettoyer(liste) }));
  if (autres.length) resultat.push({ cle: "autres", label: "Autres statuts", replie: true, entrees: nettoyer(autres) });
  return resultat;
}

/** Liste à plat, dans l'ordre du menu : [{ code, label, raison }]. */
export function listeStatuts(config, activite, pipeline, statutActuel = null) {
  return groupesStatuts(config, activite, pipeline, statutActuel).flatMap((g) => g.entrees);
}

/** Libellé du badge : la liste du tenant, sinon le libellé de l'étape du pipeline, sinon un libellé lisible (jamais le code brut). */
export function libelleStatut(code, config, activite, pipeline) {
  const dansListe = tableStatuts(config, activite)[code]?.label;
  if (dansListe) return dansListe;
  const cible = (pipeline?.legacy_mapping || {})[code] || code;
  return (pipeline?.steps || []).find((s) => s.code === cible)?.label || lisible(code);
}
